import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Trash2,
  MapPin,
  Calendar,
  DollarSign,
  Tag,
  Wrench,
  Clock,
  QrCode,
  Barcode,
  Building,
  User,
  Package,
  RefreshCw,
  Settings,
  History
} from 'lucide-react';
import { assetService } from '../../../services/assetService';
import { workOrderService } from '../../../services/workOrderService';
import ComponentsTab from '../../../components/cmms/ComponentsTab';

const AssetDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [maintenanceHistory, setMaintenanceHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchAssetData();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- helper identity changes each render; dependency list is intentional
  }, [id]);

  const fetchAssetData = async () => {
    try {
      setLoading(true);
      const assetData = await assetService.getAssetById(id);
      setAsset(assetData);

      // Try to fetch work orders for this asset
      try {
        const woData = await workOrderService.getWorkOrdersByAsset(id);
        setWorkOrders(woData || []);
      } catch (e) {
        console.log('Could not fetch work orders');
      }

      // Fetch maintenance history
      try {
        const historyData = await assetService.getAssetMaintenanceHistory(id);
        setMaintenanceHistory(historyData);
      } catch (e) {
        console.log('Could not fetch maintenance history');
      }
    } catch (err) {
      console.error('Failed to load asset:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      OPERATIONAL: 'bg-green-100 text-green-800',
      UNDER_MAINTENANCE: 'bg-yellow-100 text-yellow-800',
      OUT_OF_SERVICE: 'bg-red-100 text-red-800',
      DECOMMISSIONED: 'bg-gray-100 text-gray-800',
      IN_STORAGE: 'bg-blue-100 text-blue-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getCriticalityColor = (criticality) => {
    const colors = {
      CRITICAL: 'bg-red-100 text-red-800',
      HIGH: 'bg-orange-100 text-orange-800',
      MEDIUM: 'bg-yellow-100 text-yellow-800',
      LOW: 'bg-green-100 text-green-800',
    };
    return colors[criticality] || 'bg-gray-100 text-gray-800';
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this asset?')) {
      try {
        await assetService.deleteAsset(id);
        navigate('/portal/cmms/assets');
      } catch (err) {
        console.error('Failed to delete:', err);
        alert('Failed to delete asset');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Asset not found</p>
        <Link to="/portal/cmms/assets" className="text-primary-600 hover:underline mt-2 inline-block">
          Back to Assets
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/portal/cmms/assets')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{asset.name}</h1>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(asset.status)}`}>
                {asset.status?.replace('_', ' ')}
              </span>
            </div>
            <p className="text-gray-500 mt-1">
              {asset.assetTag && <span className="mr-3">Tag: {asset.assetTag}</span>}
              {asset.category && <span>Category: {asset.category}</span>}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <Link
            to={`/portal/cmms/assets?edit=${id}`}
            className="btn btn-outline"
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Link>
          <button onClick={handleDelete} className="btn btn-outline text-red-600 border-red-300 hover:bg-red-50">
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {['overview', 'components', 'maintenance', 'history'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-3 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Details */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <InfoItem icon={Tag} label="Asset Tag" value={asset.assetTag} />
                <InfoItem icon={Package} label="Category" value={asset.category} />
                <InfoItem icon={Settings} label="Type" value={asset.type} />
                <InfoItem icon={Building} label="Manufacturer" value={asset.manufacturer} />
                <InfoItem icon={Tag} label="Model" value={asset.model} />
                <InfoItem icon={Barcode} label="Serial Number" value={asset.serialNumber} />
                <InfoItem icon={MapPin} label="Location" value={asset.locationName} />
                <InfoItem icon={User} label="Supplier" value={asset.supplierName} />
              </div>
              {asset.description && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-sm text-gray-600">{asset.description}</p>
                </div>
              )}
            </div>

            {/* Financial Details */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold mb-4">Financial Information</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <InfoItem icon={Calendar} label="Purchase Date" value={asset.purchaseDate} />
                <InfoItem icon={DollarSign} label="Purchase Cost" value={asset.purchaseCost ? `$${asset.purchaseCost.toLocaleString()}` : null} />
                <InfoItem icon={DollarSign} label="Current Value" value={asset.currentValue ? `$${asset.currentValue.toLocaleString()}` : null} />
                <InfoItem icon={DollarSign} label="Book Value" value={asset.currentBookValue ? `$${asset.currentBookValue.toLocaleString()}` : null} />
                <InfoItem icon={Clock} label="Useful Life" value={asset.usefulLifeYears ? `${asset.usefulLifeYears} years` : null} />
                <InfoItem icon={DollarSign} label="Salvage Value" value={asset.salvageValue ? `$${asset.salvageValue.toLocaleString()}` : null} />
              </div>
            </div>

            {/* Identification */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold mb-4">Identification & Tracking</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <InfoItem icon={QrCode} label="QR Code" value={asset.qrCode} />
                <InfoItem icon={Barcode} label="Barcode" value={asset.barcode} />
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold mb-4">Status</h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Status</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(asset.status)}`}>
                    {asset.status?.replace('_', ' ')}
                  </span>
                </div>
                {asset.criticality && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Criticality</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCriticalityColor(asset.criticality)}`}>
                      {asset.criticality}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Warranty Card */}
            {asset.warrantyExpiration && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold mb-4">Warranty</h2>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Expires</span>
                    <span className="font-medium">{asset.warrantyExpiration}</span>
                  </div>
                  {asset.warrantyProvider && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Provider</span>
                      <span className="font-medium">{asset.warrantyProvider}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
              <div className="space-y-2">
                <Link
                  to={`/portal/cmms/work-orders?assetId=${id}`}
                  className="w-full btn btn-outline justify-start"
                >
                  <Wrench className="w-4 h-4 mr-2" />
                  Create Work Order
                </Link>
                <Link
                  to={`/portal/cmms/pm-schedules?assetId=${id}`}
                  className="w-full btn btn-outline justify-start"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Schedule PM
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Components Tab */}
      {activeTab === 'components' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <ComponentsTab assetId={id} assetName={asset.name} />
        </div>
      )}

      {/* Maintenance Tab */}
      {activeTab === 'maintenance' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold">Work Orders</h2>
          </div>
          {workOrders.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {workOrders.map((wo) => (
                <div key={wo.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">{wo.title}</div>
                      <div className="text-sm text-gray-500">{wo.woNumber}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        wo.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                        wo.status === 'IN_PROGRESS' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {wo.status?.replace('_', ' ')}
                      </span>
                      <Link
                        to={`/portal/cmms/work-orders/${wo.id}`}
                        className="text-primary-600 hover:text-primary-800 text-sm"
                      >
                        View
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center text-gray-500">
              No work orders found for this asset
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-6">
          {/* Cost Summary Cards */}
          {maintenanceHistory && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="text-sm text-gray-500 mb-1">Total Maintenance Cost</div>
                <div className="text-2xl font-bold text-gray-900">
                  ${(maintenanceHistory.totalCost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="text-sm text-gray-500 mb-1">Labor Cost</div>
                <div className="text-2xl font-bold text-blue-600">
                  ${(maintenanceHistory.totalLaborCost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="text-sm text-gray-500 mb-1">Parts Cost</div>
                <div className="text-2xl font-bold text-orange-600">
                  ${(maintenanceHistory.totalPartsCost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="text-sm text-gray-500 mb-1">Activities</div>
                <div className="text-2xl font-bold text-gray-900">
                  {(maintenanceHistory.totalWorkOrders || 0) + (maintenanceHistory.totalVisits || 0)}
                  <span className="text-sm font-normal text-gray-400 ml-1">
                    ({maintenanceHistory.totalWorkOrders} WO · {maintenanceHistory.totalVisits} visits)
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* History Timeline */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold">Maintenance History</h2>
            </div>

            {!maintenanceHistory || maintenanceHistory.entries?.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <History className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No maintenance history found for this asset</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {maintenanceHistory.entries.map((entry) => (
                  <div key={`${entry.type}-${entry.id}`} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 min-w-0">
                        {/* Type badge */}
                        <span className={`mt-0.5 flex-shrink-0 px-2 py-0.5 rounded text-xs font-semibold ${
                          entry.type === 'WORK_ORDER'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-purple-100 text-purple-700'
                        }`}>
                          {entry.type === 'WORK_ORDER' ? 'Work Order' : 'Visit'}
                        </span>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-gray-900 truncate">
                              {entry.title || entry.referenceNumber}
                            </span>
                            <span className="text-xs text-gray-400">{entry.referenceNumber}</span>
                          </div>
                          <div className="flex items-center gap-3 mt-1 flex-wrap">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              entry.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                              entry.status === 'IN_PROGRESS' || entry.status === 'IN_PROCESS' ? 'bg-yellow-100 text-yellow-700' :
                              entry.status === 'CANCELLED' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {entry.status?.replace(/_/g, ' ')}
                            </span>
                            {entry.date && (
                              <span className="text-xs text-gray-500 flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {entry.date}
                              </span>
                            )}
                            {entry.technicianName && (
                              <span className="text-xs text-gray-500 flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {entry.technicianName}
                              </span>
                            )}
                            {entry.supplierName && (
                              <span className="text-xs text-gray-500 flex items-center gap-1">
                                <Building className="w-3 h-3" />
                                {entry.supplierName}
                              </span>
                            )}
                            {entry.contractNumber && (
                              <span className="text-xs text-gray-400">
                                Contract: {entry.contractNumber}
                              </span>
                            )}
                            {entry.priority && (
                              <span className={`text-xs font-medium ${
                                entry.priority === 'CRITICAL' ? 'text-red-600' :
                                entry.priority === 'HIGH' ? 'text-orange-600' :
                                entry.priority === 'MEDIUM' ? 'text-yellow-600' :
                                'text-gray-500'
                              }`}>
                                {entry.priority}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Cost column */}
                      <div className="flex-shrink-0 text-right">
                        {entry.totalCost != null ? (
                          <>
                            <div className="font-semibold text-gray-900">
                              ${Number(entry.totalCost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                            {(entry.laborCost != null || entry.partsCost != null) && (
                              <div className="text-xs text-gray-400 mt-0.5">
                                {entry.laborCost != null && <span>Labor: ${Number(entry.laborCost).toFixed(2)}</span>}
                                {entry.laborCost != null && entry.partsCost != null && <span className="mx-1">·</span>}
                                {entry.partsCost != null && <span>Parts: ${Number(entry.partsCost).toFixed(2)}</span>}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-gray-400">No cost recorded</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper component for info items
const InfoItem = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-5 h-5 text-gray-400 mt-0.5" />
      <div>
        <div className="text-sm text-gray-500">{label}</div>
        <div className="font-medium text-gray-900">{value}</div>
      </div>
    </div>
  );
};

export default AssetDetail;
